import { Avatar, Box, Chip, Link, Rating, Typography } from '@mui/material'
import type { FC } from 'react'
import { useCandidateSearch } from 'recruiter/hooks'
import { recruiterRouteConfig } from 'recruiter/routes'

interface CandidateCellProps {
    candidateId?: string
    name?: string
    initials?: string
    title?: string
    profileImgKey?: string | null
    starRating?: number | null
    hasRemeetNotes?: boolean
    isActive?: boolean
}

const getAvatarColor = (initials: string): string => {
    const colors = [
        '#7E57C2',
        '#26A69A',
        '#FF7043',
        '#66BB6A',
        '#3F8FDA',
        '#EC407A',
        '#59779A',
        '#EF5350',
    ]
    const charCode =
        (initials.charCodeAt(0) || 0) + (initials.charCodeAt(1) || 0)
    return colors[charCode % colors.length]
}

const CandidateCell: FC<CandidateCellProps> = ({
    candidateId,
    name = 'Unknown Candidate',
    initials = '??',
    title,
    profileImgKey,
    starRating,
    hasRemeetNotes,
    isActive = true,
}) => {
    const { setActiveFilters } = useCandidateSearch()
    const avatarColor = getAvatarColor(initials)

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
                height: '100%',
                width: '100%',
                opacity: isActive ? 1 : 0.7,
                filter: isActive ? 'none' : 'grayscale(100%)',
            }}
        >
            <Avatar
                src={profileImgKey || undefined}
                sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    bgcolor: profileImgKey ? undefined : avatarColor,
                    flexShrink: 0,
                }}
            >
                {initials}
            </Avatar>
            <Box
                display="flex"
                flexDirection="column"
                sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}
            >
                {candidateId ? (
                    <Link
                        onClick={(e) => {
                            e.stopPropagation()
                            if (e.metaKey || e.ctrlKey) return
                            setActiveFilters?.([])
                        }}
                        href={`${recruiterRouteConfig.candidates.path}/${candidateId}`}
                        sx={{
                            textDecoration: 'none',
                            lineHeight: 1.3,
                            fontSize: '0.8125rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {name}
                    </Link>
                ) : (
                    <Typography
                        noWrap
                        sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}
                    >
                        {name}
                    </Typography>
                )}
                {(title || starRating != null || hasRemeetNotes) && (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ minWidth: 0 }}
                    >
                        {title && (
                            <Typography
                                noWrap
                                sx={{
                                    fontSize: '0.6875rem',
                                    color: 'text.secondary',
                                    lineHeight: 1.3,
                                }}
                            >
                                {title}
                            </Typography>
                        )}
                        {starRating != null ? (
                            <span
                                title={`${Math.round(starRating * 10) / 10} / 5`}
                                style={{ flexShrink: 0, display: 'flex' }}
                            >
                                <Rating
                                    value={starRating}
                                    readOnly
                                    precision={0.5}
                                    size="small"
                                    sx={{ fontSize: '0.625rem' }}
                                />
                            </span>
                        ) : hasRemeetNotes ? (
                            <Chip
                                label="RM"
                                size="small"
                                sx={{
                                    height: 16,
                                    fontSize: '0.625rem',
                                    fontWeight: 600,
                                }}
                            />
                        ) : null}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default CandidateCell
